<template>
    <MainContainer>
        <template #aboveNavSidebar>
            <p class="small mb-1">Gewähltes Schuljahr</p>
            <SchoolYearSwitcher class="mb-3" />
        </template>
        <div class="singleiter-list">
            <h2>
                Liste Singleiter
            </h2>
            <Table
                bordered
                striped
                showFilter
                :responsive="true"
                :fields="fields"
                :items="items"
                :isBusy="busy"
                busyText="Lädt Singleiter-Informationen..."
                emptyText="Keine Singleiter-Informationen gefunden"
                sortBy="mitglied_name"
                tableClass="singleiter-list-table"
            >
                <template v-slot:thead-top="{ data: data }">
                    <b-tr class="bg-light text-dark">
                        <b-th class="text-center">
                            <b-checkbox
                                v-model="multiSelect"
                                :disabled="false"
                                @change="selectAll"
                            />
                        </b-th>
                        <b-th colspan="6" class="text-left">
                            <span class="small mx-1"
                                >Auf die Gesamtauswahl bezogen:</span
                            >
                            <b-button
                                class="mx-1"
                                variant="primary"
                                :disabled="printing.busy"
                                :size="size"
                                @click="printMultipleContracts"
                                ><font-awesome-icon
                                    icon="receipt"
                                    class="text-light mr-1"
                                />Verträge drucken</b-button
                            >
                            <b-button
                                class="mx-1"
                                variant="warning"
                                :disabled="extending.busy"
                                :size="size"
                                @click="extendMultipleItems"
                                ><font-awesome-icon
                                    icon="exclamation-triangle"
                                    class="text-dark mr-1"
                                />Verträge verlängern</b-button
                            >
                        </b-th>
                        <b-th colspan="13" class="text-left">
                            <span class="small mx-1"
                                >Allgemeine Funktionen:</span
                            >
                            <b-button
                                class="mx-1"
                                variant="info"
                                :size="size"
                                @click="toggleInactive"
                            >
                                <template v-if="showInactive">
                                    <font-awesome-icon
                                        icon="eye-slash"
                                        class="text-light mr-1"
                                    />
                                    Inaktive ausblenden
                                </template>
                                <template v-else>
                                    <font-awesome-icon
                                        icon="eye"
                                        class="text-light mr-1"
                                    />
                                    Inaktive einblenden
                                </template>
                            </b-button>
                            <b-button
                                class="mx-1"
                                variant="secondary"
                                :disabled="printing.busy"
                                :size="size"
                                @click="printListPDF"
                                ><font-awesome-icon
                                    icon="print"
                                    class="text-light mr-1"
                                />Drucken</b-button
                            >
                            <b-button
                                class="mx-1"
                                variant="secondary"
                                :size="size"
                                :disabled="excel.busy"
                                @click="exportExcel"
                                ><font-awesome-icon
                                    icon="table"
                                    class="text-light mr-1"
                                />Export</b-button
                            >
                        </b-th>
                    </b-tr>
                </template>

                <template v-slot:mitglied_id="{ row: row }">
                    <template v-if="row.item.inactive || (isCurrentSchoolYear && row.item.to_date < currentDate.format('YYYY-MM-DD'))">
                        <span class="text-danger">{{ row.value }}</span>
                    </template>
                    <template v-else>
                        {{ row.value }}
                    </template>
                </template>

                <template v-slot:selected="{ row: row }">
                    <b-checkbox v-model="row.item.selected" :disabled="false" />
                </template>

                <template v-slot:mitglied_name="{ row: row }">
                    <b-link
                        :to="{
                            path: '/mitglieder/' + row.item.mitglied_id
                        }"
                        >{{ row.value }}</b-link
                    >
                </template>

                <template v-slot:schule_name="{ row: row }">
                    <b-link
                        :to="{ path: '/mitglieder/' + row.item.schule_id }"
                        >{{ row.value }}</b-link
                    >
                </template>

                <template v-slot:timespan="{ row: row }">
                    {{ $moment(row.item.from_date).format("MM/Y") }} -
                    {{ $moment(row.item.to_date).format("MM/Y") }}
                </template>

                <template v-slot:honorar_zeitraum="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:honorar_monat="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:actions="{ row: row }">
                    <b-dropdown text="Aktionen" :size="size" dropleft>
                        <b-dropdown-item
                            :to="{
                                path: '/mitglieder/' + row.mitglied_id
                            }"
                        >
                            <font-awesome-icon
                                icon="exchange-alt"
                                class="text-primary mr-1"
                            />Zum Mitglied
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="checkRight('write')"
                            :disabled="printing.busy"
                            @click="printContract(row.id)"
                        >
                            <font-awesome-icon
                                icon="receipt"
                                class="text-primary mr-1"
                            />Vetrag drucken
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="checkRight('write')"
                            :disabled="extending.busy"
                            @click="extendItem(row.id)"
                        >
                            <font-awesome-icon
                                icon="exclamation-triangle"
                                class="text-warning mr-1"
                            />Vetrag verlängern
                        </b-dropdown-item>
                    </b-dropdown>
                </template>

                <template v-slot:custom-foot="{ data: data }">
                    <b-tr class="bg-light text-dark">
                        <b-th class="text-center">
                            <b-checkbox
                                v-model="multiSelect"
                                :disabled="false"
                                @change="selectAll"
                            />
                        </b-th>
                        <b-th colspan="6" class="text-left">
                            <span class="small mx-1"
                                >Auf die Gesamtauswahl bezogen:</span
                            >
                            <b-button
                                class="mx-1"
                                variant="primary"
                                :disabled="printing.busy"
                                :size="size"
                                @click="printMultipleContracts"
                                ><font-awesome-icon
                                    icon="receipt"
                                    class="text-light mr-1"
                                />Verträge drucken</b-button
                            >
                            <b-button
                                class="mx-1"
                                variant="warning"
                                :disabled="extending.busy"
                                :size="size"
                                @click="extendMultipleItems"
                                ><font-awesome-icon
                                    icon="exclamation-triangle"
                                    class="text-dark mr-1"
                                />Verträge verlängern</b-button
                            ></b-th
                        >
                        <b-th class="text-right">
                            {{ currency(sums.perSchoolYear) }}
                        </b-th>
                        <b-th v-if="isCurrentSchoolYear">
                                &nbsp;<br>Monat: <b>{{ $moment(currentDate).format("MM/Y") }}</b>
                        </b-th>
                        <b-th v-else></b-th>
                        <b-th class="text-right">
                            &empty; {{ currency(sums.perMonth) }}
                            <div v-if="isCurrentSchoolYear">{{ currency(sums.currentMonth) }}</div>
                        </b-th>
                        <b-th class="text-center">
                            &empty; {{ sums.klassen }}
                        </b-th>
                        <b-th colspan="10"></b-th>
                    </b-tr>
                </template>
            </Table>
        </div>
    </MainContainer>
</template>

<script>
import CommonView from "@/mixins/CommonView/CommonView";
import Size from "@/mixins/Size/Size";
import Currency from "@/mixins/Currency/Currency";
import SingPause from "@/mixins/SingPause/SingPause";
import SingPauseKlassen from "@/mixins/SingPause/SingPauseKlassen";
import { ApiFactory } from "@/api/ApiFactory.js";
import { BlobUtil } from "@/utils/BlobUtil.js";
import MainContainer from "@/components/MainContainer/MainContainer";
import Table from "@/components/Table/Table";
import SchoolYearSwitcher from "@/components/SchoolYearSwitcher/SchoolYearSwitcher";

import "./SingleiterList.scss";

const SingPauseApi = ApiFactory.get("singpause");

export default {
    name: "SingleiterList",
    mixins: [CommonView, Size, Currency, SingPause, SingPauseKlassen],
    components: {
        MainContainer,
        Table,
        SchoolYearSwitcher
    },
    data() {
        return {
            fields: [
                {
                    key: "selected",
                    label: "",
                    class: "text-center",
                    sortable: false
                },
                {
                    key: "mitglied_id",
                    label: "ID",
                    sortable: true
                },
                {
                    key: "mitglied_anrede",
                    label: "Anrede",
                    sortable: false
                },
                {
                    key: "mitglied_name",
                    label: "Name",
                    sortable: true
                },
                {
                    key: "mitglied_vorname",
                    label: "Vorname",
                    sortable: true
                },
                {
                    key: "schule_name",
                    label: "Schule",
                    sortable: true
                },
                {
                    key: "timespan",
                    label: "Zeitraum",
                    class: "text-center",
                    sortable: false
                },
                {
                    key: "honorar_zeitraum",
                    label: "Honorar Zeitraum",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "monate_zeitraum",
                    label: "Monate",
                    class: "text-center",
                    sortable: true
                },
                {
                    key: "honorar_monat",
                    label: "Honorar pro Monat",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "klassen",
                    label: "Anz. Klassen",
                    class: "text-center",
                    sortable: true
                },
                {
                    key: "actions",
                    label: "Funktionen",
                    class: "text-center"
                }
            ],
            api: SingPauseApi,
            multiSelect: false,
            sums: {
                perMonth: null,
                perSchoolYear: null,
                klassen: null
            },
            showInactive: true,
            excel: {
                busy: false
            }
        };
    },
    watch: {
        selectedSchoolYear: {
            handler() {
                this.fetch();
            }
        },
        showInactive: {
            handler() {
                this.fetch();
            }
        }
    },
    methods: {
        async fetch(busyState = true) {
            this.busy = busyState;
            const response = await this.api.getSingleiterInYear(
                this.selectedSchoolYear,
                this.showInactive
            );
            if (response.ok) {
                var items = response.json.list;
                items = items
                    .filter(item => item.mitglied != null)
                    .map(item => this.process(item));
                this.items = items;
                this.registerRights(response);

                this.sums = {
                    perMonth: response.json.sum_honorar_month,
                    currentMonth: response.json.sum_honorar_current_month,
                    perSchoolYear: response.json.sum_honorar_schoolyear,
                    klassen: response.json.sum_klassen
                };
            }
            this.busy = false;
        },
        process(item) {
            for (const [key, value] of Object.entries(item.mitglied)) {
                item["mitglied_" + key] = value;
            }
            item.inactive = item.inactive == 1;
            delete item.mitglied;
            return item;
        },
        selectAll(val) {
            this.items = this.items.map(item => {
                item.selected = val;
                return item;
            });
        },
        toggleInactive() {
            this.showInactive = !this.showInactive;
        },
        async printListPDF() {
            this.printing.busy = true;
            const response = await SingPauseApi.getSingleiterInYearPDF(
                this.selectedSchoolYear,
                this.showInactive
            );
            if (response.ok) {
                BlobUtil.downloadBlob(response.blob, "Liste Singleiter.pdf");
            }
            this.printing.busy = false;
        },
        async exportExcel() {
            this.excel.busy = true;
            const response = await SingPauseApi.getSingleiterInYearExport(
                this.selectedSchoolYear,
                this.showInactive
            );
            if (response.ok) {
                BlobUtil.downloadBlob(response.blob, "Liste Singleiter.xlsx");
            }
            this.excel.busy = false;
        }
    },
    computed: {
        currentDate() {
            return this.$moment();
        },
        isCurrentSchoolYear() {
            return this.currentDate >= this.$moment(`${this.selectedSchoolYear}-09-01`) && this.currentDate <= this.$moment(`${this.selectedSchoolYear + 1}-08-31`);
        }
    }
};
</script>
